import { Bell, Star } from "lucide-react";
import { Card, CardContent } from "@baluu/ui/components/card";
import { Badge } from "@baluu/ui/components/badge";
import { Button } from "@baluu/ui/components/button";
import { Separator } from "@baluu/ui/components/separator";
import { TabSkeleton, TabError } from "../TabStatus";
import { formatDateShort, formatDateWithWeekday, formatTime } from "../format";
import type { WaitlistRow, ReviewRow } from "../types";

/** Ported from ProfileView.tsx's `WaitlistSection` (:673-730) and
 *  `ReviewsSection` (:619-670), combined into the Activity tab as in the
 *  original's Activity TabsContent (:1159-1183). */

function WaitlistSection({
  waitlist,
  onLeaveWaitlist,
  leavingId,
}: {
  waitlist: WaitlistRow[];
  onLeaveWaitlist: (id: string) => void;
  leavingId?: string;
}) {
  if (waitlist.length === 0) {
    return (
      <div className="py-8 text-center">
        <Bell className="mx-auto mb-2 h-12 w-12 text-muted-foreground/50" />
        <p className="text-muted-foreground">You&apos;re not on any waiting lists.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {waitlist.map((entry) => (
        <Card key={entry.id}>
          <CardContent className="pt-4">
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div>
                <h4 className="font-medium">{entry.courseTitle}</h4>
                {entry.sessionDate && (
                  <p className="text-sm text-muted-foreground">
                    Session: {formatDateWithWeekday(entry.sessionDate)} at {formatTime(entry.sessionDate)}
                  </p>
                )}
                <p className="mt-1 text-xs text-muted-foreground">Joined: {formatDateShort(entry.joinedAt)}</p>
                {entry.numberOfGuests > 1 && (
                  <Badge variant="outline" className="mt-1">
                    {entry.numberOfGuests} spots requested
                  </Badge>
                )}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onLeaveWaitlist(entry.id)}
                disabled={leavingId === entry.id}
                className="text-destructive hover:text-destructive"
              >
                {leavingId === entry.id ? "Leaving..." : "Leave Waitlist"}
              </Button>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function ReviewsSection({ reviews }: { reviews: ReviewRow[] }) {
  if (reviews.length === 0) {
    return (
      <div className="py-8 text-center">
        <Star className="mx-auto mb-2 h-12 w-12 text-muted-foreground/50" />
        <p className="text-muted-foreground">You haven&apos;t submitted any reviews yet.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {reviews.map((review) => (
        <Card key={review.id}>
          <CardContent className="pt-4">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="mb-1 flex items-center gap-2">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star key={i} className={`h-4 w-4 ${i < review.rating ? "fill-yellow-400 text-yellow-400" : "text-gray-300"}`} />
                  ))}
                </div>
                <h4 className="font-medium">{review.listingTitle}</h4>
                {review.reviewText && <p className="mt-1 text-sm text-muted-foreground">{review.reviewText}</p>}
              </div>
              {review.reviewType && (
                <Badge variant="secondary" className="text-xs">
                  {review.reviewType}
                </Badge>
              )}
            </div>
            <p className="mt-2 text-xs text-muted-foreground">{formatDateShort(review.createdAt)}</p>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

export function ActivityTab({
  waitlist,
  waitlistLoading,
  waitlistError,
  onRetryWaitlist,
  onLeaveWaitlist,
  leavingId,
  leaveError,
  reviews,
  reviewsLoading,
  reviewsError,
  onRetryReviews,
}: {
  waitlist: WaitlistRow[];
  waitlistLoading: boolean;
  waitlistError: boolean;
  onRetryWaitlist: () => void;
  onLeaveWaitlist: (id: string) => void;
  leavingId?: string;
  leaveError?: boolean;
  reviews: ReviewRow[];
  reviewsLoading: boolean;
  reviewsError: boolean;
  onRetryReviews: () => void;
}) {
  return (
    <div className="space-y-8">
      <div>
        <h3 className="mb-4 flex items-center gap-2 text-lg font-semibold">
          <Bell className="h-5 w-5" />
          Waiting Lists ({waitlist.length})
        </h3>
        {waitlistLoading ? (
          <TabSkeleton rows={2} />
        ) : waitlistError ? (
          <TabError onRetry={onRetryWaitlist} />
        ) : (
          <WaitlistSection waitlist={waitlist} onLeaveWaitlist={onLeaveWaitlist} leavingId={leavingId} />
        )}
        {leaveError && <p className="mt-2 text-sm text-destructive">Couldn&apos;t leave the waiting list. Please try again.</p>}
      </div>

      <Separator />

      <div>
        <h3 className="mb-4 flex items-center gap-2 text-lg font-semibold">
          <Star className="h-5 w-5" />
          Your Reviews ({reviews.length})
        </h3>
        {reviewsLoading ? <TabSkeleton rows={2} /> : reviewsError ? <TabError onRetry={onRetryReviews} /> : <ReviewsSection reviews={reviews} />}
      </div>
    </div>
  );
}
